import { useState, useEffect } from "react";
import {
  Zap, ArrowUpRight, ArrowDownLeft, Clock, CreditCard, TrendingUp,
  RefreshCw, History, CheckCircle, XCircle, AlertCircle, Building
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { toast } from "sonner";
import { CUSTOMER } from "@/constants/testIds";

const TXN_ICONS = {
  credit_purchase: <ArrowDownLeft size={14} className="text-emerald-600" />,
  sms_deduction: <ArrowUpRight size={14} className="text-red-600" />,
  admin_top_up: <ArrowDownLeft size={14} className="text-[#0c1e41]" />,
  admin_adjustment: <TrendingUp size={14} className="text-amber-600" />,
  promotional_credit: <ArrowDownLeft size={14} className="text-purple-600" />,
  subscription_bonus: <Zap size={14} className="text-blue-600" />,
};

const TXN_COLORS = {
  credit_purchase: "text-emerald-700",
  sms_deduction: "text-red-700",
  admin_top_up: "text-[#0c1e41]",
  admin_adjustment: "text-amber-700",
  promotional_credit: "text-purple-700",
  subscription_bonus: "text-blue-700",
};

const METHOD_LABELS = {
  bank_transfer: "Bank Transfer",
  paystack: "Paystack",
  flutterwave: "Flutterwave",
};

const TYPE_LABELS = {
  credit_package: "Credit Purchase",
  topup: "Top-Up",
  subscription: "Subscription",
};

const STATUS_CONFIG = {
  completed: { icon: <CheckCircle size={12} />, cls: "bg-emerald-100 text-emerald-700" },
  pending: { icon: <AlertCircle size={12} />, cls: "bg-amber-100 text-amber-700" },
  rejected: { icon: <XCircle size={12} />, cls: "bg-red-100 text-red-700" },
  failed: { icon: <XCircle size={12} />, cls: "bg-red-100 text-red-700" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { icon: <AlertCircle size={12} />, cls: "bg-slate-100 text-slate-600" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.cls}`}>
      {cfg.icon}
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
}

export default function Wallet() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("transactions");

  // Transactions state
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Payment history state
  const [payments, setPayments] = useState([]);
  const [payTotal, setPayTotal] = useState(0);
  const [payPage, setPayPage] = useState(1);
  const [payLoading, setPayLoading] = useState(false);

  useEffect(() => { loadWalletData(); }, [txPage]);
  useEffect(() => {
    if (tab === "payments") loadPaymentHistory();
  }, [tab, payPage]);

  const loadWalletData = async () => {
    setLoading(true);
    try {
      const [w, t] = await Promise.all([
        API.get("/wallet/balance"),
        API.get(`/wallet/transactions?page=${txPage}&limit=20`),
      ]);
      setWallet(w.data);
      setTransactions(t.data.transactions || []);
      setTxTotal(t.data.total || 0);
    } catch { toast.error("Failed to load wallet"); }
    finally { setLoading(false); }
  };

  const loadPaymentHistory = async () => {
    setPayLoading(true);
    try {
      const { data } = await API.get(`/payments/my-history?page=${payPage}&limit=20`);
      setPayments(data.payments || []);
      setPayTotal(data.total || 0);
    } catch { toast.error("Failed to load payment history"); }
    finally { setPayLoading(false); }
  };

  const refresh = () => {
    loadWalletData();
    if (tab === "payments") loadPaymentHistory();
  };

  const fmtAmount = (amt) => {
    if (!amt) return "—";
    return `₦${(amt / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Wallet</h1>
          <p className="text-sm text-slate-500 mt-1">Credits balance and payment history</p>
        </div>
        <Button onClick={refresh} variant="outline" size="sm" className="border-slate-300 text-slate-600 h-9 hover:bg-slate-50">
          <RefreshCw size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      {/* Balance Card */}
      <div className="bg-gradient-to-br from-[#0c1e41] to-[#060e1c] rounded-2xl p-6 relative overflow-hidden shadow-xl shadow-blue-600/20">
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-10 translate-x-10" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-10 -translate-x-6" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <Zap size={16} className="text-[#adc0e0]" />
            <span className="text-sm text-[#adc0e0]">Available Credits</span>
          </div>
          <div data-testid={CUSTOMER.walletBalance} className="text-5xl font-extrabold font-mono text-white mb-1">
            {loading ? "—" : (wallet?.balance || 0).toLocaleString()}
          </div>
          <div className="text-sm text-[#7a97c9] mb-5">SMS credits</div>
          {wallet?.reserved_balance > 0 && (
            <div className="text-xs text-[#7a97c9] mb-4">{wallet.reserved_balance} credits reserved for active campaigns</div>
          )}
          <Button
            className="bg-white text-[#0c1e41] hover:bg-[#eef1f8] font-semibold shadow-sm"
            onClick={() => navigate("/dashboard/wallet/buy")}
            data-testid={CUSTOMER.buyCreditsButton}
          >
            <CreditCard size={15} className="mr-2" /> Buy Credits
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
        <button
          data-testid="wallet-tab-transactions"
          onClick={() => setTab("transactions")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === "transactions" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        >
          Credit History
        </button>
        <button
          data-testid="wallet-tab-payments"
          onClick={() => setTab("payments")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${tab === "payments" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        >
          <History size={14} /> Payment History
        </button>
      </div>

      {/* Transaction History */}
      {tab === "transactions" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Credit History</h3>
            <span className="text-xs text-slate-500">{txTotal} entries</span>
          </div>
          {loading ? (
            <div className="divide-y divide-slate-100">
              {[...Array(5)].map((_, i) => <div key={i} className="h-14 bg-slate-100 animate-pulse mx-5 my-2 rounded" />)}
            </div>
          ) : transactions.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm">No transactions yet</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {transactions.map((t) => (
                <div key={t._id} className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors">
                  <div className="w-8 h-8 bg-slate-100 rounded-xl flex items-center justify-center shrink-0">
                    {TXN_ICONS[t.type] || <Clock size={14} className="text-slate-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-900 font-medium capitalize">{t.type?.replace(/_/g, " ")}</div>
                    <div className="text-xs text-slate-500 truncate">{t.description || t.transaction_id}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-sm font-bold font-mono ${TXN_COLORS[t.type] || "text-slate-900"}`}>
                      {t.amount > 0 ? "+" : ""}{(t.amount || 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-400">Bal: {(t.balance_after || 0).toLocaleString()}</div>
                  </div>
                  <div className="text-xs text-slate-400 hidden sm:block shrink-0 text-right w-20">
                    {t.created_at ? new Date(t.created_at).toLocaleDateString("en-NG") : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
          {Math.ceil(txTotal / 20) > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200">
              <span className="text-xs text-slate-500">Page {txPage} of {Math.ceil(txTotal / 20)}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-slate-300 text-slate-600 h-8 text-xs" disabled={txPage === 1} onClick={() => setTxPage(p => p - 1)}>Prev</Button>
                <Button size="sm" variant="outline" className="border-slate-300 text-slate-600 h-8 text-xs" disabled={transactions.length < 20} onClick={() => setTxPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment History */}
      {tab === "payments" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Payment History</h3>
            <span className="text-xs text-slate-500">{payTotal} payments</span>
          </div>
          {payLoading ? (
            <div className="divide-y divide-slate-100">
              {[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-slate-100 animate-pulse mx-5 my-2 rounded" />)}
            </div>
          ) : payments.length === 0 ? (
            <div className="py-12 text-center">
              <Building size={28} className="text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No payment records yet</p>
              <p className="text-xs text-slate-400 mt-1">Card and bank transfer payments will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payment-history-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Method</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500">Amount</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 hidden sm:table-cell">Reference</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Status</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 hidden md:table-cell">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {payments.map((p) => (
                    <tr key={p._id} data-testid="payment-history-row" className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="text-xs font-medium text-slate-800">{TYPE_LABELS[p.type] || p.type?.replace(/_/g, " ")}</div>
                        {p.package_name && <div className="text-[10px] text-slate-400 mt-0.5">{p.package_name}</div>}
                        {p.plan_name && <div className="text-[10px] text-slate-400 mt-0.5">{p.plan_name} ({p.billing_cycle})</div>}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-xs text-slate-600">{METHOD_LABELS[p.method] || p.method || "—"}</span>
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <span className="text-xs font-mono font-semibold text-slate-800">{fmtAmount(p.amount)}</span>
                      </td>
                      <td className="px-4 py-3.5 hidden sm:table-cell">
                        <span className="text-[10px] text-slate-400 font-mono">{p.reference || p.payment_id || "—"}</span>
                      </td>
                      <td className="px-4 py-3.5">
                        <StatusBadge status={p.status} />
                      </td>
                      <td className="px-5 py-3.5 text-right hidden md:table-cell">
                        <span className="text-xs text-slate-400">
                          {p.created_at ? new Date(p.created_at).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {Math.ceil(payTotal / 20) > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200">
              <span className="text-xs text-slate-500">Page {payPage} of {Math.ceil(payTotal / 20)}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-slate-300 text-slate-600 h-8 text-xs" disabled={payPage === 1} onClick={() => setPayPage(p => p - 1)}>Prev</Button>
                <Button size="sm" variant="outline" className="border-slate-300 text-slate-600 h-8 text-xs" disabled={payments.length < 20} onClick={() => setPayPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
